import {Load} from "../../common/load";
import {autoRegister, resolve} from "../../container";
import {EventBus} from "../../common/eventBus";
import {GLOBAL, type GlobalWindow} from "../../common/globals";
import {Deferred} from "../../common/utils/promises";
import {MutationObserverFactory} from "../../common/observation";
import {Configuration} from "../../common/config";
import {type PropertyMap} from "../../common/utils/objects";
import {PageScrollbar} from "../../common/pageScrollbar";
import {Cookies} from "../../common/cookies";
import {afterContentLoaded} from "../../page/elements/elementFeatures";
import type {Maybe} from "../../bootstrap/common/types";
import {ClickTracking} from "../clickTracking";

export const ONETRUST_LAYER_CLOSED_COOKIE_NAME = "OptanonAlertBoxClosed";
export const ONETRUST_CONSENT_STATUS_COOKIE_NAME = "OptanonConsent";
export const ONETRUST_CONSENT_CHANGED_EVENT = "onetrust-consent-changed";
export type OneTrustConsentChangedEventData = { marketingAccepted: boolean };

enum OneTrustCookiePrefs {
    STRICTLY_NECESSARY = "TECH",
    STATISTIC = "STAT",
    MARKETING = "MARK",
    COMFORT = "KOMF",
    SOCIAL_MEDIA = "SOC"
}

type OneTrustObject = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    OnConsentChanged: (callback: (event: CustomEvent) => void) => void;
}

export type OneTrustWindow = GlobalWindow & {
    OneTrust: OneTrustObject;
    OnetrustActiveGroups: string;
}

@autoRegister()
export class OneTrust {
    private apiLoadedDeferred: Deferred<void>;
    private mutationObserver: MutationObserver;

    private window: OneTrustWindow;
    private domainId: Maybe<string>;
    private loaded: boolean = false;

    // eslint-disable-next-line complexity
    public constructor(
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        private eventBus: EventBus = resolve(EventBus),
        private mutationObserverFactory: MutationObserverFactory = resolve(MutationObserverFactory),
        private load: Load = resolve(Load),
        private clickTracking: ClickTracking = resolve(ClickTracking),
        configuration: Configuration = resolve(Configuration),
        cookies: Cookies = resolve(Cookies)
    ) {
        this.window = GLOBAL.window() as OneTrustWindow;
        this.apiLoadedDeferred = new Deferred<void>();
        this.mutationObserver = this.mutationObserverFactory.create(() => this.handleOneTrustLoaded());

        const ONETRUST_BLOCKED = configuration.get("ONETRUST_BLOCKED") === "true";
        this.domainId = configuration.get("ONETRUST_DOMAIN_ID");

        if (!this.domainId) {
            return;
        }

        if (!ONETRUST_BLOCKED || cookies.exists(ONETRUST_LAYER_CLOSED_COOKIE_NAME)) {
            this.loadOneTrustScript();

            const oneTrustLayerFirstOpening = !cookies.exists(ONETRUST_LAYER_CLOSED_COOKIE_NAME) && cookies.exists(ONETRUST_CONSENT_STATUS_COOKIE_NAME);
            if (oneTrustLayerFirstOpening) {
                this.pageScrollbar.disablePageScrollability();
            }
        }
    }

    public loadOneTrustScript(): boolean {
        if (!this.domainId || this.loaded) {
            return true;
        }
        const oneTrustScriptAttributes: PropertyMap<string> = {
            "data-domain-script": this.domainId,
            "data-document-language": "true",
            "charset": "UTF-8"
        };
        void this.load.script(`https://cdn.cookielaw.org/scripttemplates/otSDKStub.js`, oneTrustScriptAttributes);
        this.loaded = true;
        return false;
    }

    public init(): void {
        if (this.domainId) {
            this.mutationObserver.observe(GLOBAL.bodyElement(), {childList: true, subtree: true});
        }
    }

    public async isConsentGiven(): Promise<boolean> {
        return this.apiLoadedDeferred.promise.then(() => {
            return this.window.OnetrustActiveGroups ? this.window.OnetrustActiveGroups.includes(OneTrustCookiePrefs.MARKETING) : false;
        });
    }

    private handleOneTrustLoaded(): void {
        this.replaceLogo();
        const oneTrustElement = GLOBAL.bodyElement().querySelector<HTMLElement>("#onetrust-consent-sdk");
        if (oneTrustElement && this.window.OneTrust) {
            oneTrustElement.addEventListener("click", (event: Event) => this.trackButtonsInOneTrustBanner(event));
            oneTrustElement.classList.add("pagesection");
            this.apiLoadedDeferred.resolve();
            this.mutationObserver.disconnect();
            this.window.OneTrust.OnConsentChanged((event: CustomEvent<OneTrustCookiePrefs[]>) => {
                this.pageScrollbar.enablePageScrollability();
                this.eventBus.dispatchEvent<OneTrustConsentChangedEventData>(ONETRUST_CONSENT_CHANGED_EVENT,
                    {marketingAccepted: event.detail.includes(OneTrustCookiePrefs.MARKETING)});
            });
        }
    }

    private trackButtonsInOneTrustBanner(event: Event): void {
        const clickedButton = Array.from(event.composedPath())
            .filter(el => el instanceof HTMLButtonElement)
            .filter(el => el.isVisible())
            .first();
        if (clickedButton) {
            this.clickTracking.handleButtonInteract(event, clickedButton.textContent ?? "onetrust-button-click");
        }
    }

    private replaceLogo(): void {
        const oneTrustBanner = GLOBAL.bodyElement().querySelector<HTMLElement>("#onetrust-banner-sdk");
        const headerLogo = GLOBAL.bodyElement().querySelector<HTMLElement>("header .logo-box .logo");
        if (oneTrustBanner && headerLogo && this.window.OneTrust) {
            const logoElement = oneTrustBanner.querySelector<HTMLElement>("#onetrust-policy .banner-header")!;
            logoElement.classList.add("logo-box");
            logoElement.replaceChildren(headerLogo.cloneNode(true));
        }
    }
}

afterContentLoaded(() => {
    resolve(OneTrust).init();
});